import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  styled,
} from "@mui/material";

import { ErrorBoundary } from "./ErrorBoundary";

const ACCORDION_HEADER_HEIGHT = 54;

const Accordion = styled(MuiAccordion, {
  shouldForwardProp: (prop) => prop !== "segmented",
})<{ segmented: boolean }>(({ theme, segmented }) => ({
  ...theme.typography.body2,
  color: segmented ? theme.palette.grey["800"] : "inherit",
  backgroundColor: theme.palette.grey["50"],
  boxShadow: segmented ? "none" : theme.shadows[1],
  margin: "0 0 2px 0 !important",
  cursor: "default",
  textTransform: "none",
  "&.Mui-expanded": { margin: 0, border: "none" },
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: (prop) => prop !== "segmented" && prop !== "reversed",
})<{ segmented: boolean; reversed: boolean }>(({ theme, segmented, reversed }) => ({
  ...(segmented ? {} : theme.typography.button),
  color: segmented ? "inherit" : theme.palette.primary.main,
  padding: 0,
  flexDirection: reversed ? "row-reverse" : "row",
  backgroundColor: theme.palette.grey["50"],
  boxShadow: segmented ? "none" : theme.shadows[1],
  border: segmented ? `1px solid ${theme.palette.grey["200"]}` : "none",
  minHeight: `${ACCORDION_HEADER_HEIGHT}px`,
  height: `${ACCORDION_HEADER_HEIGHT}px`,
  textTransform: !segmented ? "uppercase" : "none",
  "& .MuiAccordionSummary-expandIconWrapper": {
    width: "4rem",
    height: "100%",
    marginRight: "-1px",
    "& > svg": {
      height: "100%",
      flex: 1,
      padding: "15px",
      color: theme.palette.primary.main,
    },
  },
  "& .MuiAccordionSummary-content": {
    height: "100%",
    lineHeight: `${ACCORDION_HEADER_HEIGHT}px`,
    "& > *": {
      borderLeft: segmented ? `1px solid ${theme.palette.grey["200"]}` : "none",
      padding: `0 ${theme.spacing(2)}`,
    },
  },
  "&.Mui-expanded": { minHeight: "auto" },
}));

const AccordionDetails = styled(MuiAccordionDetails, {
  shouldForwardProp: (prop) => prop !== "segmented",
})<{ segmented: boolean }>(({ theme, segmented }) => ({
  ...theme.typography.body1,
  display: "flex",
  flexDirection: segmented ? "row" : "column",
  padding: segmented ? 0 : "20px 16px !important",
  boxShadow: segmented ? "none" : theme.shadows[1],
  borderLeft: segmented ? `1px solid ${theme.palette.grey["200"]}` : "none",
  marginLeft: segmented ? "4rem" : 0,
  marginBottom: "0",
  "& > *": {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: segmented ? theme.spacing(3) : 0,
    borderTop: segmented ? `1px solid ${theme.palette.grey["200"]}` : "none",
    borderRight: segmented ? `1px solid ${theme.palette.grey["200"]}` : "none",
    borderBottom: segmented ? `1px solid ${theme.palette.grey["200"]}` : "none",
  },
}));

export default function UtfAccordion({
  header,
  expanded = false,
  headerReversed = false,
  segmented = false,
  children = null,
  ...props
}) {
  const [isExpanded, setExpanded] = useState(expanded);

  const toggleAccordion = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Accordion expanded={isExpanded} segmented={segmented} {...props}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        reversed={headerReversed}
        segmented={segmented}
        onClick={toggleAccordion}
      >
        {header}
      </AccordionSummary>
      <AccordionDetails segmented={segmented}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </AccordionDetails>
    </Accordion>
  );
}
